import { Children, Fragment, isValidElement, useMemo, useState, type ReactNode } from 'react';
import { IcnChevron } from '../../icons/index.js';
import type { BreadcrumbItemProps, BreadcrumbRootProps } from './types.js';

/** WAI-ARIA Breadcrumb pattern.
 *  https://www.w3.org/WAI/ARIA/apg/patterns/breadcrumb/
 *
 *  Wraps children in a `<nav aria-label="…"><ol>`. Detects when the
 *  count exceeds `maxVisible` and collapses the middle items behind an
 *  expandable chip (`…`). The first and last items are always visible;
 *  a click on the chip expands the full path inline.
 *
 *  The current-page item should be rendered with `<Breadcrumb.Item
 *  current>`, which switches it from a link/button to plain text with
 *  `aria-current="page"`. */
function BreadcrumbRoot(props: BreadcrumbRootProps): ReactNode {
  const { maxVisible = Infinity, separator, className, children } = props;
  const [expanded, setExpanded] = useState<boolean>(false);
  const separatorNode = separator ?? <IcnChevron size={12} />;

  const items = useMemo(() => {
    const list: ReactNode[] = [];
    Children.forEach(children, (child) => {
      if (isValidElement(child)) list.push(child);
    });
    return list;
  }, [children]);

  const shouldCollapse = !expanded && items.length > maxVisible;
  const hiddenCount = shouldCollapse ? items.length - 2 : 0;

  const visible: Array<{ node: ReactNode; key: string | number; isOverflow?: boolean }> =
    useMemo(() => {
      if (!shouldCollapse) {
        return items.map((n, i) => ({ node: n, key: i }));
      }
      const first = items[0] ?? null;
      const last = items[items.length - 1] ?? null;
      return [
        { node: first, key: 0 },
        { node: null, key: 'overflow', isOverflow: true },
        { node: last, key: items.length - 1 },
      ];
    }, [items, shouldCollapse]);

  const rootLabel = props['aria-label'] ?? 'Breadcrumb';

  return (
    <nav
      aria-label={rootLabel}
      className={['vq-breadcrumb', className].filter(Boolean).join(' ')}
      data-component="Breadcrumb"
    >
      <ol className="vq-breadcrumb__list">
        {visible.map((entry, i) => {
          const isLast = i === visible.length - 1;
          if (entry.isOverflow === true) {
            return (
              <Fragment key="overflow">
                <li className="vq-breadcrumb__item">
                  <button
                    type="button"
                    className="vq-breadcrumb__overflow"
                    aria-label={`Show ${String(hiddenCount)} hidden items`}
                    onClick={() => {
                      setExpanded(true);
                    }}
                  >
                    …
                  </button>
                </li>
                <li className="vq-breadcrumb__separator" aria-hidden="true">
                  {separatorNode}
                </li>
              </Fragment>
            );
          }
          return (
            <Fragment key={entry.key}>
              <li className="vq-breadcrumb__item">{entry.node}</li>
              {!isLast && (
                <li className="vq-breadcrumb__separator" aria-hidden="true">
                  {separatorNode}
                </li>
              )}
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
}

function BreadcrumbItem(props: BreadcrumbItemProps): ReactNode {
  const { current, href, onClick, icon, className, children } = props;
  const content = icon === undefined
    ? children
    : (
      <>
        <span className="vq-breadcrumb__icon" aria-hidden="true">
          {icon}
        </span>
        <span className="vq-breadcrumb__label">{children}</span>
      </>
    );

  if (current === true) {
    return (
      <span
        aria-current="page"
        className={['vq-breadcrumb__current', className].filter(Boolean).join(' ')}
      >
        {content}
      </span>
    );
  }

  if (href !== undefined) {
    return (
      <a
        href={href}
        className={['vq-breadcrumb__link', className].filter(Boolean).join(' ')}
        onClick={onClick}
      >
        {content}
      </a>
    );
  }

  if (onClick === undefined) {
    return (
      <span className={['vq-breadcrumb__text', className].filter(Boolean).join(' ')}>
        {content}
      </span>
    );
  }

  return (
    <button
      type="button"
      className={['vq-breadcrumb__link', className].filter(Boolean).join(' ')}
      onClick={onClick}
    >
      {content}
    </button>
  );
}

interface BreadcrumbCompound {
  (props: BreadcrumbRootProps): ReactNode;
  Item: typeof BreadcrumbItem;
}

export const Breadcrumb: BreadcrumbCompound = Object.assign(BreadcrumbRoot, {
  Item: BreadcrumbItem,
});
